/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Icon, Typography } from "./..";
import Ripple from "../button/ripple-effect";
import PropTypes from 'prop-types';
import { forwardRef, memo, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { bgColor, borderRadius, bottom, boxBorder, breakWord, cursorDefault, cursorPointer, displayFlex, flexRow, itemsCenter, justifyCenter, justifyStart, left, mgl, mgr, outlineNone, overflowHidden, parseHeight, parseMaxWidth, parseMaxWidthHeight, parseMinWidth, parseWidth, pd, pointerEventsAuto, pointerEventsNone, positionAbsolute, positionRelative, shadowNone, textCenter, textColor, userSelectNone, whiteSpaceNoWrap } from "../../styles/general";
import { classNames } from "../../utils";
import TabContext from "./context";
const TabItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  color,
  disabled,
  endIcon,
  index,
  label,
  labelProps,
  level,
  onClick,
  startIcon,
  style,
  ...props
}, reference) => {
  const {
    tabIndexState,
    setTabIndexState,
    direction
  } = useContext(TabContext);
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const rippleRef = useRef(null);
  const isVertical = direction === 'vertical';
  const _TabItemCSS = TabItemCSS(color, isVertical);
  const _IndicatorCSS = IndicatorCSS(isVertical);
  const _onClick = e => {
    if (disabled) return;
    rippleRef.current.start(e);
    onClick ? onClick(e, index) : setTabIndexState(index);
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const renderIcon = (icon, isEndIcon) => {
    let node = icon;
    if (typeof node === 'string') {
      node = jsx(Icon, {
        color: 'currentColor',
        name: icon
      });
    }
    return jsx("span", {
      css: [IconCSS, isEndIcon && EndIconCSS]
    }, node);
  };
  return useMemo(() => {
    return jsx("div", {
      css: [_TabItemCSS, disabled && TabItemDisabledCSS, index === tabIndexState && !disabled && TabItemSelectedCSS],
      ref: ref,
      className: classNames('DGN-UI-Tab-Item', disabled && 'disabled', index === tabIndexState && !disabled && 'selected', className),
      style: style,
      onClick: _onClick,
      ...props
    }, startIcon && renderIcon(startIcon), jsx(Typography, {
      type: level,
      color: 'inherit',
      lineClamp: 1,
      hoverTooltip: true,
      ...labelProps
    }, label), children, endIcon && renderIcon(endIcon, true), jsx(Ripple, {
      ref: rippleRef,
      css: pointerEventsNone
    }), jsx("span", {
      css: _IndicatorCSS,
      className: 'Indicator'
    }));
  }, [children, className, color, disabled, endIcon, index, label, labelProps, level, onClick, props, startIcon, style, isVertical, tabIndexState]);
}));
const TabItemCSS = (color, isVertical) => css`
    ${displayFlex};
    ${flexRow};
    ${overflowHidden};
    ${itemsCenter};
    ${isVertical ? justifyStart : justifyCenter};
    ${boxBorder};
    ${cursorPointer};
    ${positionRelative};
    ${outlineNone};
    ${breakWord};
    ${whiteSpaceNoWrap};
    ${shadowNone};
    ${!isVertical && textCenter};
    ${textColor(color)};
    ${parseMinWidth(80)};
    ${parseMaxWidth(isVertical ? '100%' : 287)};
    ${pd(isVertical ? [3.5, 4] : [2, 4])}
    ${pointerEventsAuto};
    ${userSelectNone};
    flex: 0 0 auto;
    transition: all 0.2s ease-in-out, background-color 0.2s ease-in-out;
    :hover:not(.selected):not(.disabled),
    :focus {
        ${textColor('system/rest')};
        .Indicator {
            ${bgColor('system/rest')};
            transform: scale(1);
        }
    }
`;
const TabItemDisabledCSS = css`
    ${pointerEventsNone};
    ${cursorDefault};
    ${bgColor('transparent')};
    ${textColor('system/disabled')};
`;
const TabItemSelectedCSS = css`
    ${textColor('system/active')};
    .Indicator {
        ${bgColor('system/active')};
        transform: scale(1);
    }
`;
const IndicatorCSS = isVertical => css`
    ${positionAbsolute};
    ${parseHeight(isVertical ? '100%' : 4)};
    ${parseWidth(isVertical ? 4 : '100%')}
    ${borderRadius(1)};
    ${bottom(0)};
    ${left(0)};
    transform: ${isVertical ? 'scaleY(0)' : 'scaleX(0)'};
    transition: transform 0.2s ease-in-out;
`;
const IconCSS = css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    ${justifyCenter};
    ${positionRelative};
    ${boxBorder};
    ${parseMaxWidthHeight(24)};
    ${mgr([1])};
`;
const EndIconCSS = css`
    ${mgr('unset')};
    ${mgl([1])};
`;
TabItem.defaultProps = {
  className: '',
  color: 'system/rest',
  disabled: false,
  label: '',
  style: {}
};
TabItem.propTypes = {
  /** Element to display in Tabs (like label). */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** Color for Tabs. */
  color: PropTypes.string,
  /** If true, the component is disabled. */
  disabled: PropTypes.bool,
  /** Icon to the right of the button. */
  endIcon: PropTypes.node,
  /** Index received in position of TabHeaderButton. */
  index: PropTypes.number,
  /** Data string, if any. */
  label: PropTypes.string,
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/typography--title) applied to the `Typography` label. */
  labelProps: PropTypes.object,
  /** Callback fired when a "click" event is detected. */
  onClick: PropTypes.func,
  /** Icon to the left of the button. */
  startIcon: PropTypes.node,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Any props else. */
  props: PropTypes.any
};
export default TabItem;